/**
 * File: src/pages/game-detail-page.js
 * Mô tả: Trang chi tiết sản phẩm (/games/:id), bao gồm breadcrumb, media và wiki hướng dẫn, hỗ trợ i18n.
 * Liên quan: src/components/game-wiki.js, src/i18n/i18n-service.js
 */

import { createElement } from '../utils/dom.js';
import { games } from '../data/games.js';
import { renderGameWiki } from '../components/game-wiki.js';
import { openLightbox } from '../state/modal-state.js';
import { handleImageError } from '../utils/media.js';
import { getLanguage, t, onLanguageChange } from '../i18n/i18n-service.js';

export const renderGameDetailPage = (slug = 'ant-clash') => {
  const container = createElement('div', { className: 'game-detail-page' });
  const game = games.find((g) => g.id === slug) || games[0];

  const heroSection = createElement('section', {
    className: 'section',
    attributes: { style: 'padding-top: calc(72px + 2rem); background: var(--bg-secondary);' },
  });

  const renderContent = () => {
    const lang = getLanguage();
    const description = lang === 'en' && game.id === 'ant-clash'
      ? 'Ant Clash is a fast-paced 2D real-time strategy (RTS) game. Command an ant colony to battle aggressive termites and capture enemy nests across 55 unique campaign levels.'
      : (lang === 'en' ? game.fullDescriptionEn : null) || game.fullDescription;

    const playStoreLink = game.platformLinks?.find((p) => p.name === 'Google Play' && p.available && p.url && p.url !== '#');
    const websiteLink = game.platformLinks?.find((p) => p.name === 'xiangqionline.com' && p.available && p.url && p.url !== '#');

    heroSection.innerHTML = `
      <div class="container">
        <div style="font-size: 0.9rem; color: var(--color-text-dim); margin-bottom: 1rem;">
          <a href="/" style="color: var(--color-accent);">${t('gameDetail.breadcrumbHome')}</a> / <a href="/#games" style="color: var(--color-accent);">${t('gameDetail.breadcrumbGames')}</a> / <strong>${game.title}</strong>
        </div>

        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 320px), 1fr)); gap: 2rem; align-items: center;">
          <div>
            <span class="card-genre">${lang === 'en' ? game.genreEn || game.genre : game.genre} • ${lang === 'en' ? game.releaseDateEn || game.releaseDate : game.releaseDate}</span>
            <div style="display: flex; align-items: center; gap: 16px; margin: 0.5rem 0 1rem;">
              ${game.icon ? `<img src="${game.icon}" alt="${game.title} Icon" style="width: 60px; height: 60px; border-radius: 14px; object-fit: cover; box-shadow: 0 4px 16px rgba(0,0,0,0.4);" />` : ''}
              <h1 style="font-size: 3rem; margin: 0;">${game.title}</h1>
            </div>
            <p style="font-size: 1.1rem; color: var(--color-text-muted); line-height: 1.7; margin-bottom: 1.5rem;">
              ${description}
            </p>

            <h2 style="font-size: 1.4rem; margin-bottom: .5rem;">Gameplay</h2>
            <p style="color: var(--color-text-muted); margin-bottom: 1rem;">${lang === 'en' ? game.gameplayEn : game.gameplay}</p>
            <p style="margin-bottom: 1rem;">${lang === 'en' ? 'Platforms' : 'Nền tảng'}: ${game.platforms}</p>
            ${!game.platformLinks.length ? `<p class="card-status">${lang === 'en' ? 'Coming soon — release date to be announced' : 'Sắp ra mắt — chưa công bố ngày phát hành'}</p>` : ''}
            <div style="display: flex; gap: 1rem; flex-wrap: wrap;">
              ${
                playStoreLink
                  ? `<a href="${playStoreLink.url}" target="_blank" rel="noopener noreferrer" class="btn btn-primary" style="display: inline-flex; align-items: center; gap: 8px;">
                      <span>${lang === 'en' ? 'Get it on Google Play' : 'Tải trên Google Play'}</span>
                    </a>`
                  : ''
              }
              ${websiteLink ? `<a href="${websiteLink.url}" target="_blank" rel="noopener noreferrer" class="btn ${playStoreLink ? 'btn-secondary' : 'btn-primary'}"><span>${lang === 'en' ? 'Play at xiangqionline.com ↗' : 'Chơi tại xiangqionline.com ↗'}</span></a>` : ''}
              ${game.id === 'beatglide' ? `<a href="/beatglide/privacy" class="btn btn-secondary">${lang === 'en' ? 'Privacy Policy' : 'Quyền riêng tư'}</a>` : ''}
              ${game.id === 'ant-clash' ? `<a href="#wiki" class="btn ${playStoreLink ? 'btn-secondary' : 'btn-primary'}">
                <span>${t('gameDetail.readWiki')}</span>
              </a>` : ''}
            </div>
          </div>

          <div style="border-radius: var(--radius-lg); overflow: hidden; border: var(--border-glass); aspect-ratio: 16/9; background: #000;">
            ${
              game.trailerUrl?.endsWith('.mp4')
                ? `<video src="${game.trailerUrl}"${game.trailerPoster ? ` poster="${game.trailerPoster}"` : ''} controls playsinline preload="metadata" style="width: 100%; height: 100%; object-fit: contain;"></video>`
                : `<img src="${game.coverImage}" alt="${game.title}" style="width: 100%; height: 100%; object-fit: contain;" />`
            }
          </div>
        </div>

        <div style="margin-top: 3rem;">
          <h3 style="margin-bottom: 1rem;">${t('gameDetail.screenshots')}</h3>
          <div class="screenshot-grid" id="page-screenshots-container"></div>
        </div>
      </div>
    `;

    const documents = document.createElement('div');
    documents.className = 'game-documents';
    documents.innerHTML = game.documents.length ? `<h2 style="font-size: 1.4rem; margin: 2rem 0 1rem;">${lang === 'en' ? 'Game guides' : 'Tài liệu hướng dẫn'}</h2>` : '';
    game.documents.forEach((doc) => {
      const link = document.createElement('a');
      link.href = doc.url; link.textContent = doc.title;
      link.target = '_blank'; link.rel = 'noopener noreferrer';
      link.className = 'company-email'; link.style.display = 'block';
      documents.appendChild(link);
    });
    heroSection.querySelector('.container').appendChild(documents);
    const screenshotsContainer = heroSection.querySelector('#page-screenshots-container');
    if (screenshotsContainer && game.screenshots) {
      game.screenshots.forEach((imgUrl, index) => {
        const thumb = createElement('div', { className: 'screenshot-thumb', attributes: { role: 'button', tabindex: '0', 'aria-label': `${game.title} Screenshot ${index + 1}` } });
        thumb.innerHTML = `<img src="${imgUrl}" alt="${game.title} Screenshot ${index + 1}" />`;
        const imgEl = thumb.querySelector('img');
        handleImageError(imgEl);
        thumb.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); openLightbox(game.screenshots, index); }
        });
        thumb.addEventListener('click', () => openLightbox(game.screenshots, index));
        screenshotsContainer.appendChild(thumb);
      });
    }
  };

  renderContent();
  onLanguageChange(() => renderContent());

  container.appendChild(heroSection);
  if (game.id === 'ant-clash') container.appendChild(renderGameWiki());

  return container;
};
